import { format, subDays, getDay, startOfQuarter, endOfQuarter } from 'date-fns'
import type {
  IProfileMetrics,
  IProfileMetricsV2,
  MetricsDataPoint,
} from '~/types/wpnStore'

/**
 * Generates deterministic, realistic-looking mock data for the profile metrics
 * dashboard.
 *
 * Data is seeded off the store ID, so a given store always renders the same
 * series while different stores vary. Series use smooth value noise (plateaus,
 * gentle drift, and weekend seasonality) rather than random points, so the
 * charts read like real store activity. Levels and tiers are spread across the
 * stores generated together, so any two of them compare as clearly different.
 * Both the V1 (`IProfileMetrics`) and V2 (`IProfileMetricsV2`) schemas are
 * supported.
 */

// Default history length. Covers the 30/90-day filters; the cumulative
// "previous trend" compares against up to 2x the active filter.
const DEFAULT_DAYS = 90

const REVIEW_KEYS = [
  'staff',
  'product',
  'management',
  'attendance',
  'environment',
  'community',
  'other',
] as const

// StandardMetrics isn't exported from the schema, so mirror its shape here.
type MetricSeries = {
  current: MetricsDataPoint
  history: MetricsDataPoint[]
  trend: { value: number; percentage: number }
}

/**
 * Hashes a string into a 32-bit unsigned integer to seed the PRNG.
 *
 * @param str String to hash (the store ID)
 * @returns Unsigned 32-bit seed
 */
function hashSeed(str: string): number {
  let h = 1779033703 ^ str.length
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  return h >>> 0
}

/**
 * Creates a seeded mulberry32 PRNG.
 *
 * @param seed Unsigned 32-bit seed
 * @returns Function returning deterministic floats in [0, 1)
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Draws a stable 0-1 factor for a store from its own seed, so it spreads evenly
 * across stores instead of depending on where the main series stream happens to
 * be.
 *
 * @param storeId Store ID used to seed the data
 * @param key Namespace, so unrelated factors don't correlate
 * @returns Deterministic float in [0, 1)
 */
function storeFactor(storeId: string, key: string): number {
  return mulberry32(hashSeed(`${storeId}:${key}`))()
}

// Small math helpers.
const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const smoothstep = (t: number) => t * t * (3 - 2 * t)
const round2 = (v: number) => Math.round(v * 100) / 100
const round3 = (v: number) => Math.round(v * 1000) / 1000
const mean = (a: number[]) =>
  a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0
const iso = (d: Date) => format(d, 'yyyy-MM-dd')

interface SeriesConfig {
  min: number
  max: number
  base: number // series center / mean-reversion target
  volatility: number // step size between anchors, in absolute units
  segment: number // days per anchor (larger = flatter/longer plateaus)
  integer?: boolean
  weekend?: number // weekend multiplier for count-style metrics
}

/**
 * Builds a smooth value-noise series from oldest to today (ascending). Anchors
 * spaced every `segment` days are interpolated with smoothstep, and repeated
 * anchors form plateaus.
 *
 * @param rand Seeded PRNG
 * @param days Number of daily points to generate
 * @param today Reference date for the most recent point
 * @param cfg Series shape configuration
 * @returns Ascending array of dated data points
 */
function generateSeries(
  rand: () => number,
  days: number,
  today: Date,
  cfg: SeriesConfig,
): MetricsDataPoint[] {
  const { min, max, base, volatility, segment, integer, weekend } = cfg
  const span = max - min

  const anchorCount = Math.ceil(days / segment) + 2
  const anchors: number[] = [clamp(base, min, max)]
  for (let a = 1; a < anchorCount; a++) {
    const prev = anchors[a - 1]
    if (rand() < 0.3) {
      anchors.push(prev) // plateau
      continue
    }
    const step = (rand() * 2 - 1) * volatility
    const revert = (base - prev) * 0.25 // pull back toward center
    anchors.push(clamp(prev + step + revert, min, max))
  }

  const points: MetricsDataPoint[] = []
  for (let i = 0; i < days; i++) {
    const pos = i / segment
    const idx = Math.floor(pos)
    const t = smoothstep(pos - idx)
    let v = lerp(anchors[idx], anchors[idx + 1] ?? anchors[idx], t)
    v += (rand() * 2 - 1) * span * 0.03 // daily jitter
    const date = subDays(today, days - 1 - i)
    if (weekend && (getDay(date) === 0 || getDay(date) === 6)) v *= weekend
    v = clamp(v, min, max)
    points.push({ date: iso(date), value: integer ? Math.round(v) : round3(v) })
  }
  return points
}

/**
 * Computes the trailing 7-day rolling sum of a daily series, used for the V2
 * `_weekly` metrics.
 *
 * @param daily Ascending daily series
 * @returns Ascending series where each point is the sum of the prior 7 days
 */
function rollingWeeklySum(daily: MetricsDataPoint[]): MetricsDataPoint[] {
  return daily.map((d, i) => {
    let sum = 0
    for (let j = Math.max(0, i - 6); j <= i; j++) sum += daily[j].value ?? 0
    return { date: d.date, value: Math.round(sum) }
  })
}

/**
 * Wraps an ascending daily series into the schema's metric shape. History is
 * stored newest-first (the component reverses it back), current is today's
 * point, and the trend compares the last 14 days against the prior 14, clamped
 * to a realistic +/-60% so noisy daily counts don't produce absurd badges.
 *
 * @param daily Ascending daily series
 * @param integer Whether values should be rounded to whole numbers
 * @returns Metric object with current, history, and trend
 */
function toMetricSeries(
  daily: MetricsDataPoint[],
  integer: boolean,
): MetricSeries {
  const vals = daily.map((d) => d.value ?? 0)
  const recent = mean(vals.slice(-14))
  const prior = mean(vals.slice(-28, -14))
  const delta = recent - prior
  return {
    current: daily[daily.length - 1],
    history: [...daily].reverse(),
    trend: {
      value: integer ? Math.round(delta) : round3(delta),
      percentage: prior ? clamp(round3(delta / prior), -0.6, 0.6) : 0,
    },
  }
}

/**
 * Maps a 0-1 quality score to a performance-category label.
 *
 * @param score Normalized score between 0 and 1
 * @returns 'Excellent' | 'Good' | 'Needs Improvement'
 */
function categoryFor(score: number): string {
  return score > 0.66 ? 'Excellent' : score > 0.4 ? 'Good' : 'Needs Improvement'
}

/**
 * Builds a seeded count for each review category.
 *
 * @param rand Seeded PRNG
 * @param scale Upper bound for the generated counts
 * @returns Map of review category to count
 */
function buildReviews(rand: () => number, scale: number) {
  return REVIEW_KEYS.reduce(
    (acc, key) => {
      acc[key] = Math.round(rand() * scale)
      return acc
    },
    {} as Record<(typeof REVIEW_KEYS)[number], number>,
  )
}

interface GenerateOptions {
  /** Force schema version. Defaults to a per-store split (odd hash gives V2). */
  version?: 1 | 2
  /** History length in days (default 90). */
  days?: number
  /** Stores generated together; levels spread evenly across the set. */
  peers?: string[]
}

/**
 * Generates a full metrics payload for a single store. Deterministic per
 * `storeId`; returns the V1 or V2 shape depending on the resolved version.
 *
 * @param storeId Store ID used to seed the data
 * @param options Optional version and history-length overrides
 * @returns A V1 or V2 profile metrics payload
 */
export function generateTestProfileMetrics(
  storeId: string,
  options: GenerateOptions = {},
): IProfileMetrics | IProfileMetricsV2 {
  const seed = hashSeed(storeId)
  const rand = mulberry32(seed)
  const days = options.days ?? DEFAULT_DAYS
  const version = options.version ?? (seed % 2 === 0 ? 1 : 2)
  const today = new Date()
  const peers = options.peers?.includes(storeId) ? options.peers : [storeId]

  /**
   * Resolves a store's 0-1 level for a metric. Stores are ranked against their
   * peers by their own seeded factor and each takes an even slice of the range,
   * so any two stores land clearly apart instead of colliding by chance. The
   * ranking is per metric, so a store isn't uniformly high or low across all of
   * them.
   *
   * @param key Metric namespace to rank on
   * @returns Level in (0, 1), well separated from the other peers' levels
   */
  const levelFor = (key: string) => {
    const ranked = [...peers].sort(
      (a, b) => storeFactor(a, key) - storeFactor(b, key),
    )
    const rank = ranked.indexOf(storeId)
    return (rank + 0.15 + storeFactor(storeId, key) * 0.7) / ranked.length
  }

  // Per-store levels, spread across a wide range so two stores read as clearly
  // different. Each series then sits in a fixed band around its own base, so the
  // shape stays as smooth as it would at any other level.
  const csatBase = lerp(0.78, 0.95, levelFor('csat'))
  const csatDaily = generateSeries(rand, days, today, {
    min: csatBase - 0.06,
    max: Math.min(csatBase + 0.05, 0.99),
    base: csatBase,
    volatility: 0.035,
    segment: 12,
  })
  const calendarBase = lerp(0.62, 0.96, levelFor('calendar'))
  const calendarDaily = generateSeries(rand, days, today, {
    min: calendarBase - 0.12,
    max: Math.min(calendarBase + 0.08, 1),
    base: calendarBase,
    volatility: 0.1,
    segment: 14,
  })
  const eventDiversityBase = lerp(13, 28, levelFor('variety'))
  const eventDiversityDaily = generateSeries(rand, days, today, {
    min: eventDiversityBase - 5,
    max: eventDiversityBase + 5,
    base: eventDiversityBase,
    volatility: 2.5,
    segment: 10,
    integer: true,
  })
  const ticketsBase = lerp(760, 1010, levelFor('tickets'))
  const ticketsDaily = generateSeries(rand, days, today, {
    min: ticketsBase - 70,
    max: ticketsBase + 90,
    base: ticketsBase,
    volatility: 22,
    segment: 16,
    integer: true,
  })
  const newPlayersBase = lerp(44, 74, levelFor('newPlayers'))
  const newPlayersDaily = generateSeries(rand, days, today, {
    min: newPlayersBase - 9,
    max: newPlayersBase + 11,
    base: newPlayersBase,
    volatility: 4,
    segment: 16,
    integer: true,
  })
  const reviewsCountBase = lerp(0.5, 4, levelFor('reviews'))
  const reviewsCountDaily = generateSeries(rand, days, today, {
    min: 0,
    max: reviewsCountBase + 3,
    base: reviewsCountBase,
    volatility: 1.4,
    segment: 8,
    integer: true,
  })

  // Store tier: taken from the peer-ranked level rather than wherever a random
  // walk lands, so stores being compared don't share a tier. Rolling adds slow
  // progress toward the next tier; tier 6 is maxed out.
  const tier = 1 + Math.floor(clamp(levelFor('tier'), 0, 0.999) * 6)
  const tierProgressDaily = generateSeries(rand, days, today, {
    min: 0,
    max: 0.98,
    base: 0.2 + rand() * 0.6,
    volatility: 0.12,
    segment: 20,
  })
  const tierDaily = tierProgressDaily.map((d) => ({
    date: d.date,
    value: round3(Math.min(tier + (d.value ?? 0), 6)),
  }))
  const rollingTier = tierDaily[tierDaily.length - 1].value ?? tier
  const tierScore = (tier - 1) / 5
  // Signed week-over-week change (drives the tier trend badge).
  const tierTrend = round2(
    rollingTier - (tierDaily[tierDaily.length - 8]?.value ?? rollingTier),
  )
  const store_tier = {
    current: {
      date: iso(today),
      value: tier,
    },
    history: [...tierDaily].reverse(),
    trend: tierTrend,
    rolling: round3(rollingTier),
    delta: tierTrend,
  }

  const meta = {
    exportedAt: today.toISOString(),
    version,
    organizationId: storeId,
    timezone: 'America/Los_Angeles',
    seriesType: 'daily',
    windowDays: days,
    asOfDate: iso(today),
  }
  const next_updated_quarter_date = iso(endOfQuarter(today))
  const last_updated_quarter_date = iso(startOfQuarter(today))

  if (version === 1) {
    const data: IProfileMetrics = {
      meta,
      next_updated_quarter_date,
      last_updated_quarter_date,
      customer_experience_performance_category: categoryFor(
        ((csatDaily[csatDaily.length - 1].value ?? 0.7) - 0.7) / 0.3,
      ),
      play_performance_category: categoryFor(rand() * 0.5 + tierScore * 0.5),
      community_performance_category: categoryFor(
        calendarDaily[calendarDaily.length - 1].value ?? 0,
      ),
      store_tier,
      reviews: buildReviews(rand, 22),
      metrics: {
        csat: toMetricSeries(csatDaily, false),
        tickets: toMetricSeries(ticketsDaily, true),
        new_to_op_players: toMetricSeries(newPlayersDaily, true),
        calendar_up_to_date: toMetricSeries(calendarDaily, false),
        event_diversity: toMetricSeries(eventDiversityDaily, true),
      },
    }
    return data
  }

  const data: IProfileMetricsV2 = {
    meta,
    next_updated_quarter_date,
    last_updated_quarter_date,
    customer_experience_performance_category: categoryFor(
      ((csatDaily[csatDaily.length - 1].value ?? 0.7) - 0.7) / 0.3,
    ),
    new_players_performance_category: categoryFor(
      rand() * 0.5 + tierScore * 0.5,
    ),
    tickets_performance_category: categoryFor(rand() * 0.5 + tierScore * 0.5),
    event_variety_performance_category: categoryFor(
      ((eventDiversityDaily[eventDiversityDaily.length - 1].value ?? 0) - 5) /
        25,
    ),
    on_time_event_schedule_performance_category: categoryFor(
      calendarDaily[calendarDaily.length - 1].value ?? 0,
    ),
    store_tier,
    reviews_positive: buildReviews(rand, 16),
    reviews_negative: buildReviews(rand, 5),
    metrics: {
      csat: toMetricSeries(csatDaily, false),
      tickets: toMetricSeries(ticketsDaily, true),
      tickets_weekly: toMetricSeries(rollingWeeklySum(ticketsDaily), true),
      new_to_op_players: toMetricSeries(newPlayersDaily, true),
      new_to_op_players_weekly: toMetricSeries(
        rollingWeeklySum(newPlayersDaily),
        true,
      ),
      calendar_up_to_date: toMetricSeries(calendarDaily, false),
      event_diversity: toMetricSeries(eventDiversityDaily, true),
      reviews_count: toMetricSeries(reviewsCountDaily, true),
      reviews_count_weekly: toMetricSeries(
        rollingWeeklySum(reviewsCountDaily),
        true,
      ),
    },
  }
  return data
}

/**
 * Builds the `{ ids, metrics }` response the dashboard expects, generating a
 * mock payload per store ID. Mirrors the `/api/new-profile-metrics` shape.
 *
 * @param storeIds Store IDs to generate data for
 * @param options Optional version and history-length overrides
 * @returns Response object keyed by store ID
 */
export function generateTestProfileMetricsResponse(
  storeIds: string[],
  options: GenerateOptions = {},
): {
  ids: string[]
  metrics: { [key: string]: IProfileMetrics | IProfileMetricsV2 | null }
} {
  const metrics: {
    [key: string]: IProfileMetrics | IProfileMetricsV2 | null
  } = {}
  // Pass the full set so levels spread across it instead of colliding by chance.
  for (const id of storeIds) {
    metrics[id] = generateTestProfileMetrics(id, {
      ...options,
      peers: storeIds,
    })
  }
  return { ids: [...storeIds], metrics }
}
